import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { formatBlockSummary, type TTooltipPlotLine } from 'App/dashboards/_utils/buildPestTooltip'
import type { SeriesOptionsType } from 'highcharts/highstock'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { alphabeticalSort } from 'utils/alphabeticalSort'

const SPRAY_PLOT_LINE_ID_PREFIX = 'DASHBOARD_SPRAY_PLOTLINE_'

// Spray tooltip content per the design, on a single row: an "Applied Spray" label followed by the
// "{n} blocks: A, B, +N more" block summary.
const buildSprayTooltipHtml = (blockNames: string[]): string => {
  const summary = formatBlockSummary(blockNames)

  return `${translate.phrases.banyanApp('Applied Spray')}${summary ? ` — ${summary}` : ''}`
}

/**
 * Flattens spray records to one vertical plot line per unique spray day, restricted to the
 * blocks rendered in the chart. The records are already filtered to the dashboard's insect
 * server-side (via /insect-spray-records), so we only narrow further to the chart's blocks.
 * Each line carries a tooltipDisplay listing the blocks sprayed that day.
 */
export const buildSprayPlotLines = ({
  sprayRecords,
  blockIds,
  blockNamesById,
  timezone,
}: {
  sprayRecords: routes.InsectSprayRecords.Response | null
  blockIds: number[]
  blockNamesById: Record<number, string>
  timezone: string
}): { plotLines: TTooltipPlotLine[]; legendSeries: SeriesOptionsType | null } => {
  if (!sprayRecords || sprayRecords.length === 0 || blockIds.length === 0) {
    return { plotLines: [], legendSeries: null }
  }

  const blockIdSet = new Set(blockIds)
  // dayStartMs -> set of block ids sprayed that day (within the chart's blocks).
  const blockIdsByDay = new Map<number, Set<number>>()

  sprayRecords.forEach((record) => {
    record.blocks.forEach((block) => {
      if (!blockIdSet.has(block.blockId)) return

      block.sprays.timeseries.forEach((spray) => {
        const dayMs = +moment.tz(spray.timestamp, timezone).startOf('day')
        const blocksForDay = blockIdsByDay.get(dayMs) ?? new Set<number>()

        blocksForDay.add(block.blockId)

        blockIdsByDay.set(dayMs, blocksForDay)
      })
    })
  })

  if (blockIdsByDay.size === 0) return { plotLines: [], legendSeries: null }

  const plotLines: TTooltipPlotLine[] = Array.from(blockIdsByDay.entries()).map(([dayMs, blockIdsForDay]) => {
    const blockNames = Array.from(blockIdsForDay)
      .map((id) => blockNamesById[id])
      .filter(Boolean)
      .sort(alphabeticalSort)

    return {
      id: `${SPRAY_PLOT_LINE_ID_PREFIX}${dayMs}`,
      color: colors.sprayRecordGreen,
      value: dayMs,
      width: 2,
      dashStyle: 'ShortDot',
      zIndex: 3,
      tooltipDisplay: () => buildSprayTooltipHtml(blockNames),
    }
  })

  // An empty line series purely to give "Applied Sprays" a legend entry; clicking it toggles
  // the plot lines on the shared xAxis (Highcharts plot lines have no native legend toggle).
  const legendSeries: SeriesOptionsType = {
    type: 'line',
    id: 'dashboard-applied-sprays',
    name: translate.phrases.banyanApp('Applied Sprays'),
    color: colors.sprayRecordGreen,
    dashStyle: 'ShortDot',
    data: [],
    marker: { enabled: false },
    events: {
      legendItemClick: function () {
        const xAxis = this.chart.xAxis?.[0]

        if (!xAxis) return

        const willHide = this.visible

        if (willHide) {
          plotLines.forEach((pl) => pl.id && xAxis.removePlotLine(pl.id))
        } else {
          plotLines.forEach((pl) => xAxis.addPlotLine(pl))
        }
      },
    },
  }

  return { plotLines, legendSeries }
}
